import React, { useEffect, useMemo, useState } from 'react';
import { GenericDetailPage } from '../../components/generic';
import { flattenPersonalFolders, useAccessibleAccounts, usePersonalFolders } from '../emails/useData';
import { useEmailRule, type EmailRule } from './useData';
import { moduleConfig } from './config';
import { getEmailRuleDetailSections, getEmailRuleStatusBadges } from './emailRuleDetail';
import api from '../../services/api';

const Detail: React.FC = () => {
  const { data: accounts } = useAccessibleAccounts();
  const { data: personalFolders } = usePersonalFolders();
  const [userNames, setUserNames] = useState<Map<number, string>>(new Map());

  useEffect(() => {
    api
      .get<
        Array<{
          id: number;
          userDisplayName?: string | null;
          userFirstName?: string;
          userLastName?: string;
          userEmail: string;
        }>
      >('/users')
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : [];
        const map = new Map<number, string>();
        for (const user of list) {
          const label =
            user.userDisplayName?.trim() ||
            [user.userFirstName, user.userLastName].filter(Boolean).join(' ').trim() ||
            user.userEmail;
          map.set(user.id, label);
        }
        setUserNames(map);
      })
      .catch(() => setUserNames(new Map()));
  }, []);

  const accountById = useMemo(() => {
    const map = new Map<number, string>();
    for (const account of accounts ?? []) {
      map.set(account.id, account.displayName || account.emailAddress);
    }
    return map;
  }, [accounts]);

  const personalFolderNames = useMemo(() => {
    const map = new Map<number, string>();
    for (const folder of flattenPersonalFolders(personalFolders ?? [])) {
      map.set(folder.id, folder.name);
    }
    return map;
  }, [personalFolders]);

  const detailContext = useMemo(
    () => ({ accountById, personalFolderNames, userNames }),
    [accountById, personalFolderNames, userNames],
  );

  const getDetailSections = (item: EmailRule, formatDate: (dateString?: string) => string) =>
    getEmailRuleDetailSections(item, formatDate, detailContext);

  return (
    <GenericDetailPage<EmailRule>
      useData={useEmailRule}
      config={{
        ...moduleConfig,
        getDetailSections,
        getStatusBadges: getEmailRuleStatusBadges,
      }}
    />
  );
};

export default Detail;
